import type {
  PresentedTokenValue,
  TokenValuePresentation,
  TokenValuePresentations,
} from "@src/components/DefuseSDK/types/asset"
import type {
  TokenInfo,
  TokenValue,
} from "@src/components/DefuseSDK/types/base"
import { formatTokenValue } from "./format"
import { isBaseToken, isUnifiedToken } from "./token"

export const TOKEN_VALUE_UNAVAILABLE_LABEL = "Value unavailable"

function getTokenDecimals(token: TokenInfo): number | null {
  if (isBaseToken(token)) return token.decimals
  return token.groupedTokens.find(isBaseToken)?.decimals ?? null
}

function trimDecimalInput(value: string): string {
  return value.includes(".")
    ? value.replace(/0+$/, "").replace(/\.$/, "")
    : value
}

export function parseDisplayNumber(value: string): number {
  const parsed = Number.parseFloat(value.replace(/,/g, ""))
  return Number.isFinite(parsed) ? parsed : 0
}

export function tokenAmountToValueInput(
  tokenAmount: string,
  unitPrice: number
): string {
  const value = parseDisplayNumber(tokenAmount) * unitPrice
  if (!Number.isFinite(value) || value <= 0) return ""
  return trimDecimalInput(value.toFixed(6))
}

export function tokenAmountToValueDisplay(
  tokenAmount: string,
  unitPrice: number
): string {
  const value = parseDisplayNumber(tokenAmount) * unitPrice
  if (!Number.isFinite(value) || value <= 0) return ""
  return trimDecimalInput(value.toFixed(value < 0.01 ? 6 : 2))
}

export function valueInputToTokenAmount(
  valueInput: string,
  token: TokenInfo,
  unitPrice: number
): string {
  if (valueInput === "") return ""

  const value = parseDisplayNumber(valueInput)
  const decimals = getTokenDecimals(token)
  if (!Number.isFinite(value) || value <= 0 || decimals == null) return ""

  const tokenAmount = value / unitPrice
  if (!Number.isFinite(tokenAmount) || tokenAmount <= 0) return ""

  return trimDecimalInput(tokenAmount.toFixed(decimals))
}

export function formatValueAmount(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return "0"
  if (value < 0.01) return "< 0.01"

  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

export function getTokenValuePresentation(
  token: TokenInfo | null | undefined,
  presentations: TokenValuePresentations | undefined
): TokenValuePresentation | undefined {
  if (token == null || presentations == null) return undefined

  if (isBaseToken(token)) {
    return presentations[token.defuseAssetId]
  }

  if (isUnifiedToken(token)) {
    return (
      presentations[token.unifiedAssetId] ??
      token.groupedTokens
        .map((groupedToken) => presentations[groupedToken.defuseAssetId])
        .find((presentation) => presentation != null)
    )
  }

  return undefined
}

export function presentTokenAmount(
  amount: string,
  token: TokenInfo,
  presentation: TokenValuePresentation | null | undefined
): { amount: string; symbol: string } {
  if (
    presentation?.usdcPrice == null ||
    !Number.isFinite(presentation.usdcPrice) ||
    presentation.usdcPrice <= 0
  ) {
    return { amount, symbol: token.symbol }
  }
  const presentedAmount = tokenAmountToValueDisplay(
    amount,
    presentation.usdcPrice
  )
  return presentedAmount === ""
    ? { amount, symbol: token.symbol }
    : { amount: presentedAmount, symbol: presentation.unitSymbol }
}

export function presentTokenValue(
  amount: TokenValue,
  presentation: TokenValuePresentation
): PresentedTokenValue {
  if (amount.amount === 0n) {
    return {
      status: "ready",
      value: 0,
      amount: "0",
      symbol: presentation.unitSymbol,
      label: `~0 ${presentation.unitSymbol}`,
    }
  }

  if (presentation.usdcPrice == null || presentation.usdcPrice <= 0) {
    return { status: "unavailable", label: TOKEN_VALUE_UNAVAILABLE_LABEL }
  }

  const tokenAmount = Number.parseFloat(
    formatTokenValue(amount.amount, amount.decimals)
  )
  const value = tokenAmount * presentation.usdcPrice
  if (!Number.isFinite(value) || value < 0) {
    return { status: "unavailable", label: TOKEN_VALUE_UNAVAILABLE_LABEL }
  }

  const displayAmount = formatValueAmount(value)
  return {
    status: "ready",
    value,
    amount: displayAmount,
    symbol: presentation.unitSymbol,
    label: `~${displayAmount} ${presentation.unitSymbol}`,
  }
}
